import {
  Label,
  Input,
  Toaster,
  makeStyles,
  Tooltip,
  Button,
} from "@fluentui/react-components";
import MaterialReactTable, {
  MRT_Cell,
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_VisibilityState,
} from "material-react-table";
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import "./style.css";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import SavePopup from "./savePopup";
import { debounce, isEqual } from "lodash";
import moment from "moment";
import { convertStringToArray, formatedDate } from "../../../utils";
import HighlightText from "../../../components/HighlightText/index";
import ASCButton from "../../../components/Button";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { dateOptions } from "../../../shared/commissionDateOptions";
import { TableListLoader } from "../../../components/Loader/TableListLoader";
import {
  fetchDestributionList,
  postDistribution,
  setAppliedSelectedDates,
  setSelectedParties,
  setFilteredData,
  postStopDistribution,
} from "../../../redux/Commission/distributionSlice";
import {
  selectTablePagination,
  selectTableSorting,
  selectTableColumnOrder,
  selectTableColumnVisibility,
  selectTableAppliedFilters,
  selectIsDefaultPreference,
  setTablePagination,
  setTableSorting,
  setTableColumnOrder,
  setTableColumnVisibility,
  setTableDraftFilter,
  applyTableFilters,
  saveTableState,
  resetTableStateToDefault,
} from "../../../redux/preferenceSlice";
import defaultTableConfig from "../../../shared/tableConfig.json";
import FilterTab from "./FilterTab";
import RecieptForm from "../CommissionReceipts/RecieptForm";
import apiServices from "../../../service";
import ConfirmStopDistributionPopup from "./ConfirmStopDistributionPopup";
import CommissionDetailDrawer from "./CommissionDetailDrawer";
import {
  DocumentBulletList20Regular,
  ArrowClockwise20Regular,
} from "@fluentui/react-icons";

const TABLE_NAME = "COMMISSION_DISTRIBUTION" as const;

const HIGHLIGHT_TRANSFORMS: Record<string, (v: any) => string> = {
  date: formatedDate,
};

const makeHighlightCell =
  (transformKey: keyof typeof HIGHLIGHT_TRANSFORMS) =>
  ({ row, column }: any) => (
    <HighlightText
      text={HIGHLIGHT_TRANSFORMS[transformKey](row.original[column.id])}
      highlight={String(column.getFilterValue() ?? "")}
    />
  );

const makeHighlightFilterFn =
  (transformKey: keyof typeof HIGHLIGHT_TRANSFORMS) =>
  (row: any, columnId: string, filterValue: any) =>
    HIGHLIGHT_TRANSFORMS[transformKey](row.getValue(columnId))
      .toLowerCase()
      .includes(String(filterValue).trim().toLowerCase());

interface SelectOption {
  value: string;
  label: string;
}
interface SelectField {
  field: string;
  options: SelectOption[];
}

const Distributionlist = () => {
  const { showToast, toasterId } = useToastMessage();
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [isOpenDistributionDetailDrawer, setIsOpenDistributionDetailDrawer] = useState(false);
  const [
    confirmationStopDistributionPopup,
    setStopDistributionConfirmationPopup,
  ] = useState(false);
  const [stopReceiptpopup, setStopReceiptpopup] = useState(false);
  const [amountReceived, setAmountReceived] = useState("");
  const [distributedAmount, setDistributedAmount] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState<any[]>([]);
  const [receiptEditpopup, setReceiptEditpopup] = useState(false);
  const [filterPopup, setFilterPopup] = useState(false);
  const [confirmsavepopup, setConfirmsavepopup] = useState(false);
  const [activeRowItem, setActiveRowItem] = useState<any>({});
  const [dateOption, setDateOption] = useState<string>("last12Months");
  const [selectPartiesFieldOptions, setSelectPartiesFieldOptions] = useState<
    SelectField[]
  >([]);

  const toastTimeout = useRef(false);
  const dispatch: AppDispatch = useDispatch();

  const distributionList: any[] = useSelector(
    (state: RootState) => state.distribution.listItems || [],
  );

  const selectedPartyNames: any = useSelector(
    (state: RootState) => state.distribution.selectedPartyNames,
  );

  const isLoading: any = useSelector(
    (state: RootState) => state.distribution.loading.distribution || false,
  );
  const saveLoading: any = useSelector(
    (state: RootState) => state.distribution.loading.postDistribution || false,
  );
  const saveError: any = useSelector(
    (state: RootState) => state.distribution.error.postDistribution || "",
  );

  const postStopDistributionLoading: any = useSelector(
    (state: RootState) => state.distribution.loading.stopDistribution,
  );

  const loggedUser: any = useSelector((state: RootState) => state.authUser);
  const selectedDateValue: any = useSelector(
    (state: RootState) => state.distribution.appliedSelectedDates,
  );
  const totalRows: any = useSelector(
    (state: RootState) => state.distribution.totalRows,
  );
  const [editedActualValues, setEditedActualValues] = useState<{
    [key: string]: string;
  }>({});

  /* ================= TABLE PREFERENCE STATE ================= */
  const { preferences, status: preferencesStatus } = useSelector(
    (state: RootState) => state.preferences,
  );
  const pagination = useSelector(selectTablePagination(TABLE_NAME));
  const sorting = useSelector(selectTableSorting(TABLE_NAME));
  const columnOrder = useSelector(selectTableColumnOrder(TABLE_NAME));
  const columnVisibility = useSelector(selectTableColumnVisibility(TABLE_NAME));
  const preferenceAppliedFilters = useSelector(selectTableAppliedFilters(TABLE_NAME));
  const isDefaultPreference = useSelector(selectIsDefaultPreference(TABLE_NAME));
  const savedConfig = useMemo(
    () => preferences.tables.contextSpecific[TABLE_NAME],
    [preferences.tables.contextSpecific],
  );
  const tableConfig =
    preferences.tables.contextSpecific[TABLE_NAME] ??
    (defaultTableConfig.tables.contextSpecific as any)[TABLE_NAME];

  const hasTableChanges = useMemo(() => {
    if (!savedConfig) return false;
    return !isEqual(
      {
        filter: preferenceAppliedFilters,
        pagination,
        sorting,
        columnOrder,
        columnVisibility: columnVisibility ?? {},
      },
      {
        filter: savedConfig.filter,
        pagination: savedConfig.initialState?.pagination,
        sorting: savedConfig.initialState?.sorting,
        columnOrder: savedConfig.initialState?.columnOrder,
        columnVisibility: savedConfig.initialState?.columnVisibility ?? {},
      },
    );
  }, [preferenceAppliedFilters, pagination, sorting, columnOrder, columnVisibility, savedConfig]);

  /* ================= FILTER <-> PREFERENCE MIRROR ================= */
  // Distribution's live filter UI (FilterTab + distributionSlice) stays the
  // source of truth for the current session. Date range filtering is
  // session-only and intentionally NOT part of the saved preference — only
  // the party selection mirrors into preferenceSlice (for Save/Reset) and,
  // once on load, hydrates back out of the saved preference.
  const filterHydratedRef = useRef(false);
  const skipNextFilterMirrorRef = useRef(false);

  useEffect(() => {
    if (preferencesStatus !== "succeeded" || filterHydratedRef.current) return;
    filterHydratedRef.current = true;
    skipNextFilterMirrorRef.current = true; // this hydration's state change must not re-trigger applyTableFilters (which would reset pageIndex)
    dispatch(setSelectedParties(preferenceAppliedFilters?.parties ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferencesStatus]);

  useEffect(() => {
    if (!filterHydratedRef.current) return;
    if (skipNextFilterMirrorRef.current) {
      skipNextFilterMirrorRef.current = false;
      return;
    }
    dispatch(setTableDraftFilter({ tableName: TABLE_NAME, filter: { parties: selectedPartyNames } }));
    dispatch(applyTableFilters({ tableName: TABLE_NAME }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartyNames]);

  const handlePaginationChange = (
    updater:
      | MRT_PaginationState
      | ((prev: MRT_PaginationState) => MRT_PaginationState),
  ) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    dispatch(setTablePagination({ tableName: TABLE_NAME, pagination: next }));
  };

  const handleSortingChange = (
    updater: MRT_SortingState | ((prev: MRT_SortingState) => MRT_SortingState),
  ) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    dispatch(setTableSorting({ tableName: TABLE_NAME, sorting: next }));
  };

  const handleColumnOrderChange = (
    updater: string[] | ((prev: string[]) => string[]),
  ) => {
    const next = typeof updater === "function" ? updater(columnOrder) : updater;
    dispatch(setTableColumnOrder({ tableName: TABLE_NAME, columnOrder: next }));
  };

  const handleColumnVisibilityChange = (
    updater: MRT_VisibilityState | ((prev: MRT_VisibilityState) => MRT_VisibilityState),
  ) => {
    const next = typeof updater === "function" ? updater(columnVisibility) : updater;
    dispatch(setTableColumnVisibility({ tableName: TABLE_NAME, columnVisibility: next }));
  };

  const handleSavePreference = async () => {
    try {
      await dispatch(
        saveTableState({ tableName: TABLE_NAME, shouldRefetch: true }),
      ).unwrap();
      showToast("User preferences have been updated successfully.", "", "success");
    } catch {
      showToast("Failed to save preferences", "", "error");
    }
  };

  const handleResetToDefault = async () => {
    try {
      await dispatch(resetTableStateToDefault({ tableName: TABLE_NAME })).unwrap();
      skipNextFilterMirrorRef.current = true; // the filter reset below must not immediately re-save via the mirror effect
      dispatch(setSelectedParties([]));
      showToast("User preferences have been reset to default.", "", "success");
    } catch {
      showToast("Failed to reset preferences", "", "error");
    }
  };

  const handleDistributionDetailPopup = (open: boolean) => {
    setIsOpenDistributionDetailDrawer(open);
  };


  useEffect(() => {
    if (stopReceiptpopup) {
      setConfirmsavepopup(true);
    }
  }, [stopReceiptpopup]);

  const { userId } = useSelector((state: RootState) => state.authUser);

  const fetchParams = useMemo(
    () => ({
      userId: userId,
      dateValue: selectedDateValue || "",
      partyName: selectedPartyNames?.map((p: any) => p.id).join(",") || "",
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [
      userId,
      selectedDateValue,
      selectedPartyNames,
      pagination.pageIndex,
      pagination.pageSize,
    ],
  );

  const debouncedFetchDistributionList = useCallback(
    debounce((params) => {
      if (userId) {
        dispatch(fetchDestributionList(params));
      }
    }, 500),
    [dispatch],
  );

  const fetchDistributionListImmediate = useCallback(
    (params: any) => {
      debouncedFetchDistributionList?.cancel();
      if (userId) {
        dispatch(fetchDestributionList(params));
      }
    },
    [dispatch, debouncedFetchDistributionList],
  );

  const getSelectionListOptions = async () => {
    try {
      const fieldLbl =
        "commission_party_type:FixedList,party_name:MasterDatawithNewEntry,true_false:FixedList";
      const res = await apiServices.selectionList.getOptions(fieldLbl);

      if (res.data && res.data.data) {
        const responseData = res.data.data;
        let formattedOptions: SelectField[] = [];

        if (Array.isArray(responseData)) {
          formattedOptions = responseData;
        } else if (typeof responseData === "object") {
          formattedOptions = Object.keys(responseData).map((key) => ({
            field: key,
            options: responseData[key],
          }));
        }

        setSelectPartiesFieldOptions(formattedOptions);
      } else {
        setSelectPartiesFieldOptions([]);
      }
    } catch (error) {
      console.error("Error fetching selection options:", error);
      showToast("Failed to load dropdown options", "", "error");
      setSelectPartiesFieldOptions([]);
    }
  };

  const handleStopDistribution = async ({ stop_distribution_date }: any) => {
    if (stop_distribution_date && selectedRowIds?.length > 0) {
      const [commissionAllocationId, receiptId] = selectedRowIds[0].split("/");
      // const formattedDate = moment(stop_distribution_date).format("YYYY-MM-DD");

      try {
        const payload: any = {
          commissionAllocationId,
          stop_date: stop_distribution_date,
        };

        const resultAction = await dispatch(postStopDistribution(payload));
        if (postStopDistribution.rejected.match(resultAction)) {
          const errorMsg: any = Array.isArray(resultAction.payload)
            ? resultAction.payload.join(", ")
            : Array.isArray(resultAction.error?.message)
              ? resultAction.error?.message.join(", ")
              : resultAction.payload ||
              resultAction.error?.message ||
              "Failed to stop distribution";
          showToast(errorMsg, "", "error");
          return;
        }

        const result = resultAction.payload;
        if (result?.isError) {
          const apiMessage = Array.isArray(result?.error?.message)
            ? result.error.message.join(", ")
            : result?.error?.message ||
            result?.message ||
            "Failed to stop distribution";
          showToast(apiMessage, "", "error");
        } else {
          showToast("Distribution stopped successfully!", "", "success");
          setStopDistributionConfirmationPopup(false);
          setReceiptEditpopup(false);
          fetchDistributionListImmediate(fetchParams);
        }
      } catch (error: any) {
        console.error("error", error);
        const errMsg = Array.isArray(error?.message)
          ? error.message.join(", ")
          : error?.message || "Failed to stop distribution";
        showToast(errMsg, "", "error");
      }
    } else {
      showToast("Please select a row and valid stop date", "", "error");
    }
  };

  useEffect(() => {
    if (loggedUser?.userId && filterPopup) {
      getSelectionListOptions();
    }
  }, [filterPopup]);

  useEffect(() => {
    if (preferencesStatus !== "succeeded") return;
    debouncedFetchDistributionList(fetchParams);
    return () => {
      debouncedFetchDistributionList?.cancel();
    };
  }, [fetchParams, debouncedFetchDistributionList, preferencesStatus]);

  const CELL_RENDERER_MAP: Record<string, (props: any) => JSX.Element> = {
    highlightedDate: makeHighlightCell("date"),
    distributionActualInput: ({ cell, row }: { cell: MRT_Cell<any>; row: any }) => {
      const allocationId = String(row.original.commissionAllocationId);
      const receiptId = String(row.original.commissonreciptid);
      const uniqueKey = `${allocationId}/${receiptId}`;

      const allocated = selectedRowIds?.some(
        (id: string) => id === uniqueKey,
      );

      return (
        <Input
          style={{ border: "none" }}
          className="hover-border"
          value={
            editedActualValues[uniqueKey] ??
            cell.getValue<string>() ??
            ""
          }
          type="number"
          onChange={(e) => {
            if (allocated) {
              if (!toastTimeout.current) {
                showToast(
                  "You must clear the 'allocated' checkbox before changing the 'actual' amount.",
                  "",
                  "error",
                );
                toastTimeout.current = true;

                setTimeout(() => {
                  toastTimeout.current = false;
                }, 2000);
              }
              return;
            }

            const newValue = e.target.value;

            setEditedActualValues((prev) => ({
              ...prev,
              [uniqueKey]: newValue,
            }));
          }}
        />
      );
    },
  };

  const FILTER_FN_MAP: Record<string, (row: any, columnId: any, filterValue: any) => boolean> = {
    highlightedDate: makeHighlightFilterFn("date"),
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    return (tableConfig.columns ?? []).map((col: any) => ({
      ...col,
      ...(col.Cell && typeof col.Cell === "string"
        ? { Cell: CELL_RENDERER_MAP[col.Cell] }
        : {}),
      ...(col.filterFn && typeof col.filterFn === "string"
        ? { filterFn: FILTER_FN_MAP[col.filterFn] }
        : {}),
    })) as MRT_ColumnDef<any>[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableConfig, editedActualValues, selectedRowIds]);

  const sortingConfig = tableConfig.sortingConfig;
  const finalColumns = useMemo(
    () =>
      sortingConfig?.sortableColumns
        ? columns.map((col: any) => ({
            ...col,
            enableSorting: sortingConfig.sortableColumns.includes(col.accessorKey),
          }))
        : columns,
    [columns, sortingConfig],
  );

  const getOptionsByField = (fieldName: string): SelectOption[] => {
    const field = selectPartiesFieldOptions.find(
      (opt) => opt.field === fieldName,
    );
    return field?.options || [];
  };

  const mappedPartyOptions = useMemo(
    () =>
      getOptionsByField("party_name")
        .filter((opt) => opt.label && opt.value) // remove null/undefined/empty
        .map((opt) => ({
          label: opt.label,
          id: opt.value,
          checked: false,
        })),
    [getOptionsByField], // or the dependencies that actually change
  );

  // Update distributedAmount for selected rows
  useEffect(() => {
    const total = selectedRowIds.reduce((sum, key) => {
      const [allocationId, receiptId] = key.split("/");
      const row = distributionList.find(
        (item) =>
          String(item.commissionAllocationId) === allocationId &&
          String(item.commissonreciptid) === receiptId,
      );
      if (!row) return sum;
      const actualValue = editedActualValues[key] ?? row.expected ?? 0;
      return sum + parseFloat(actualValue);
    }, 0);

    setDistributedAmount(total);
  }, [selectedRowIds, distributionList, editedActualValues]);

  useEffect(() => {
    if (selectedRowIds?.length === 1) {
      const [allocationId, receiptId] = selectedRowIds[0].split("/");
      const selectedRow = distributionList.find(
        (item) =>
          item.commissionAllocationId === allocationId &&
          String(item.commissonreciptid) === receiptId,
      );
      if (selectedRow) {
        setActiveRowItem(selectedRow);
        dispatch(setFilteredData(selectedRow));
      }
    } else {
      setActiveRowItem({});
      dispatch(setFilteredData({}));
    }
  }, [selectedRowIds, distributionList, dispatch]);

  // Handle save for single or multiple selected rows
  const handleSave = async (data: { receipted_date: Date }) => {
    try {
      if (!data?.receipted_date) {
        showToast("Please select a valid receipted date", "", "error");
        return false;
      }
      const formattedDate = moment(data.receipted_date).format("YYYY-MM-DD");
      const rowsToUpdateAndPost = selectedRowIds
        .map((key) => {
          const [commissionAllocationId, receiptId] = key.split("/");

          const row = distributionList.find(
            (item) =>
              String(item.commissionAllocationId) === commissionAllocationId &&
              String(item.commissonreciptid) === receiptId,
          );
          if (!row) return null;

          const editedActual = editedActualValues[commissionAllocationId];
          const originalActual = row.actual;
          const expectedValue = row.expected;
          const hasActualChanged =
            editedActual !== undefined && editedActual !== originalActual;

          const finalActualAmount = hasActualChanged
            ? editedActual
            : (originalActual ?? expectedValue ?? 0);

          return {
            row,
            commissionAllocationId,
            receiptId,
            actualAmount: finalActualAmount,
            shouldUpdate: hasActualChanged,
          };
        })
        .filter(Boolean) as {
          row: (typeof distributionList)[number];
          commissionAllocationId: string;
          actualAmount: any;
          receiptId: string;
        }[];

      const payload = {
        allocation_payments: rowsToUpdateAndPost.map(
          ({ row, commissionAllocationId, receiptId, actualAmount }) => ({
            receipt_id: receiptId,
            commission_allocation_id: commissionAllocationId,
            paid_amount:
              actualAmount !== null && actualAmount !== undefined
                ? Number(parseFloat(actualAmount).toFixed(4))
                : null,
            paid_date: formattedDate,
          }),
        ),
      };

      // 3️ Post distribution
      const result = await dispatch(postDistribution({ formData: payload }));
      if (postDistribution.fulfilled.match(result)) {
        showToast(result.payload.message, "", "success");
        fetchDistributionListImmediate(fetchParams);
        setEditedActualValues({});
        setSelectedRowIds([]);
        setAmountReceived("");
        setConfirmsavepopup(false);
        return true;
      } else {
        showToast(
          `Failed to save: ${saveError || "Unknown error"}`,
          "",
          "error",
        );
        return false;
      }
      // return true;
    } catch (error: any) {
      showToast("Error in handleSave:", "", error);
      return false;
    }
  };

  const selectedMenuItem = useMemo(() => {
    const dateLabel =
      dateOptions.find((opt) => opt.value === dateOption)?.label ||
      "Last 12 Months";
    let formattedDateLabel = dateLabel;
    if (dateOption === "custom" && selectedDateValue) {
      formattedDateLabel = `Custom`;
    }
    return selectedPartyNames?.length > 0 || selectedDateValue
      ? `${formattedDateLabel}, Parties (${selectedPartyNames?.length})`
      : "Last 12 Months, Parties";
  }, [selectedDateValue, selectedPartyNames]);

  const clearFilters = () => {
    dispatch(setAppliedSelectedDates(null));
    dispatch(setSelectedParties([]));
    setDateOption("");
    fetchDistributionListImmediate({
      userId: userId,
      dateValue: "",
      partyName: "",
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    });
  };

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <div className="commission_options">
        <div className="commission_filter">
          <Label>Amount for Distribution</Label>
          <Input
            value={distributedAmount.toString()}
            onChange={(e) => setAmountReceived(e.target.value)}
            type="number"
            disabled
            placeholder="Enter amount"
            className="custom-disabled-input"
          />
          {!convertStringToArray(
            process.env.REACT_APP_HIDE_CTA_BUTTON,
          ).includes("hide_cm_receipting_auto_apply") && !isLoading && (
              <ASCButton
                shape="rounded"
                appearance="outline"
                disabled={
                  isLoading ||
                    !amountReceived ||
                    distributionList?.length === 0 ||
                    !convertStringToArray(
                      process.env.REACT_APP_DISABLE_CTA_BUTTON,
                    ).includes("disable_cm_receipting_auto_apply")
                    ? false
                    : true
                }
                onClick={() => {
                  const allIds = distributionList.map(
                    (row) =>
                      `${row.commissionAllocationId}/${row.commissonreciptid}`,
                  );

                  setSelectedRowIds((prev) => {
                    if (prev?.length === allIds.length) {
                      return [];
                    }
                    return allIds;
                  });
                }}
                label="Distribute All"
              />
            )}
        </div>

        <div style={{ display: "flex", gap: "10px" }}>

          {/* <ASCButton
            shape="rounded"
            size="medium"
            appearance="outline"
            disabled={convertStringToArray(
              process.env.REACT_APP_DISABLE_CTA_BUTTON
            ).includes("asset_case_details_disable") || selectedRowIds?.length !== 1}
            icon={<DocumentBulletList20Regular />}
            onClick={() => {
              if (selectedRowIds?.length === 1) {
                setIsOpenDistributionDetailDrawer(true);
              }
            }}
            label={"Distribution Details"}
          /> */}

          <ASCButton
            shape="rounded"
            className="asc-button-primary"
            appearance="primary"
            disabled={selectedRowIds?.length === 0}
            onClick={() => setConfirmsavepopup(true)}
            label={saveLoading ? "Distributing..." : "Distribute"}
            data-testid="distribute-button"
          />
        </div>
      </div>

      <div className="commission_module">
        {isLoading ? (
          <TableListLoader rows={10} columns={6} />
        ) : (
          <MaterialReactTable
            enableColumnResizing={true}
            renderTopToolbarCustomActions={() => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {!convertStringToArray(
                  process.env.REACT_APP_HIDE_CTA_BUTTON,
                ).includes("hide_cm_receipting_adjust_commission") && (
                    <ASCButton
                      shape="rounded"
                      className="asc-button-primary"
                      appearance="primary"
                      onClick={() => setReceiptEditpopup(true)}
                      disabled={Number(selectedRowIds?.length) !== 1}
                      label="Adjust Distribution"
                    />
                  )}
                <FilterTab
                  title="Filter"
                  partyOptions={mappedPartyOptions}
                  selectedMenuItem={selectedMenuItem}
                  getReceiptlistData={() =>
                    debouncedFetchDistributionList(fetchParams)
                  }
                  filterPopup={filterPopup}
                  setFilterPopup={setFilterPopup}
                  dateOption={dateOption}
                  setDateOption={setDateOption}
                />

                <ASCButton
                  shape="rounded"
                  className="asc-button-primary fill_button"
                  appearance="primary"
                  onClick={clearFilters}
                  label="Clear filters"
                  disabled={
                    !selectedDateValue &&
                    (!selectedPartyNames?.length ||
                      selectedPartyNames?.length === 0)
                  }
                />
                {!convertStringToArray(
                  process.env.REACT_APP_HIDE_CTA_BUTTON,
                ).includes("hide_cm_distribution_preference") && (
                    <>
                      <ASCButton
                        label="Save Preference"
                        shape="rounded"
                        appearance="secondary"
                        onClick={handleSavePreference}
                        disabled={!hasTableChanges}
                      />
                      <Tooltip content="Reset to Default" relationship="label" positioning="below">
                        <Button
                          icon={<ArrowClockwise20Regular />}
                          onClick={handleResetToDefault}
                          disabled={isDefaultPreference}
                        />
                      </Tooltip>
                    </>
                  )}
              </div>
            )}
            columns={finalColumns}
            rowCount={totalRows}
            data={distributionList}
            enableColumnFilters={true}
            enableGlobalFilter={false}
            enableDensityToggle={false}
            enableRowSelection={true}
            enableSelectAll={true}
            enableColumnActions={false}
            enableColumnOrdering
            enableColumnDragging
            enableFullScreenToggle={true}
            enablePagination={true}
            onPaginationChange={handlePaginationChange}
            onSortingChange={handleSortingChange}
            onColumnOrderChange={handleColumnOrderChange}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            state={{ pagination, sorting, columnOrder, columnVisibility }}
            manualPagination
            enableStickyHeader
            enablePinning
            initialState={{
              columnPinning: {
                left: ["mrt-row-select", "expected", "actual"],
              },
              density: "compact",
            }}
            displayColumnDefOptions={{
              "mrt-row-select": {
                header: "Select",
                size: 50,
              },
            }}
            muiSelectCheckboxProps={({ row }: any) => ({
              checked: selectedRowIds.includes(
                `${row.original.commissionAllocationId}/${row.original.commissonreciptid}`,
              ),

              color: "primary",
              onChange: () => {
                const key = `${row.original.commissionAllocationId}/${row.original.commissonreciptid}`;
                setSelectedRowIds((prev) =>
                  prev.includes(key)
                    ? prev.filter((id) => id !== key)
                    : [...prev, key],
                );
              },
            })}
            muiTablePaginationProps={{
              rowsPerPageOptions: [10, 50, 100, 200, 500],
            }}
            muiSelectAllCheckboxProps={{
              checked:
                selectedRowIds?.length === distributionList?.length &&
                distributionList?.length > 0,
              indeterminate:
                selectedRowIds?.length > 0 &&
                selectedRowIds?.length < distributionList?.length,
              onChange: (event) => {
                if (event.target.checked) {
                  setSelectedRowIds(
                    distributionList.map(
                      (row) =>
                        `${row.commissionAllocationId}/${row.commissonreciptid}`,
                    ),
                  );
                } else {
                  setSelectedRowIds([]);
                }
              },
            }}
            renderEmptyRowsFallback={() => {
              return (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "calc(100vh - 360px)",
                    color: "#f00",
                  }}
                >
                  No matching records found for the entered search criteria
                </div>
              );
            }}
            positionActionsColumn="last"
            positionToolbarAlertBanner="none"
          />
        )}
      </div>

      <RecieptForm
        setReceiptEditpopup={setReceiptEditpopup}
        setConfirmationPopup={setConfirmationPopup}
        setStopDistributionConfirmationPopup={
          setStopDistributionConfirmationPopup
        }
        tab="distribution"
        setStopReceiptpopup={setStopReceiptpopup}
        receiptEditpopup={receiptEditpopup}
        getReceiptlistData={() => debouncedFetchDistributionList(fetchParams)}
        data={activeRowItem}
        notify={showToast}
      />

      <ConfirmStopDistributionPopup
        confirmationStopDistributionPopup={confirmationStopDistributionPopup}
        setStopDistributionConfirmationPopup={
          setStopDistributionConfirmationPopup
        }
        onSave={handleStopDistribution}
        postStopDistributionLoading={postStopDistributionLoading}
      />

      <SavePopup
        action="stop_distribution"
        confirmsavepopup={confirmsavepopup}
        setConfirmsavepopup={setConfirmsavepopup}
        onSave={handleSave}
        saveError={saveError}
        saveLoading={saveLoading}
      />

      <CommissionDetailDrawer
        activeItem={activeRowItem}
        handleDistributionDetailPopup={handleDistributionDetailPopup}
        isOpenDistributionDetailDrawer={isOpenDistributionDetailDrawer}

      />
    </>
  );
};

export default Distributionlist;
